/**
 * Vault sync enrolment — multi-device sync Part A
 * (app/plans/01_08_2026/vault-multidevice/PLAN.md §A1-§A4).
 *
 * Mirrors the established pattern every other per-account sync family uses
 * (`ensurePersonalSync`, `personalSync.js`), called from `useSyncEngine`'s
 * auth-settle effect. The vault differs from personal/team sync in exactly
 * one way: it has a secret gate, and the gate can be dismissed — so it needs
 * the auth-settle hook AND an unlock-settle hook; personal sync needs
 * neither, because it has no gate.
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { useAuthStore } from '../store/authStore';
import { choiceOf, readConsent, CONSENT_NEEDED_EVENT } from './vaultCloudConsent';
import { registerThisDevice } from './vaultDevices';
import { recordFeature, VAULT } from './telemetry';

/**
 * Card 6.3': the account's cloud-copy choice decides whether this device may
 * enrol. 'device' → never enrol, and stop an enrolled device; 'ask' → the
 * dialog asks, an already-enrolled device (a paid account that synced before
 * the question existed) keeps syncing meanwhile, a new one waits; offline
 * (null) → an enrolled device keeps today's behaviour, a new one waits.
 */
async function cloudConsent() {
  try {
    return await readConsent();
  } catch {
    return null;
  }
}

/**
 * Idempotent: enrols this device's vault into the signed-in account's scope
 * if it isn't already, then (re-)starts the engine task. No-op unless the
 * vault is unlocked and protected — most calls, at most points in the app's
 * life, will be a no-op via `vault_sync_status.eligible === false`, exactly
 * like `ensurePersonalSync`'s early return when signed out.
 */
export async function ensureVaultSync() {
  if (!isTauri()) return;
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || user?.id == null) return;
  let status;
  try {
    status = await invoke('vault_sync_status');
  } catch (err) {
    console.warn('[vault-sync] status read failed:', err);
    return;
  }
  // vault_absent | vault_not_protected | vault_locked
  if (!status?.eligible) return;
  const consent = await cloudConsent();
  const choice = consent ? choiceOf(consent) : null;
  if (choice === 'device') {
    if (status.enrolled && status.scopeId) {
      await invoke('sync_unregister_workspace', { workspaceId: status.scopeId }).catch(() => {});
      // The cloud copy was deleted (on this device or another one): forget
      // the server's acks and cursor too, or a later "Store in my cloud" would
      // upload only the rows it never acked (code review of #475).
      const deleted = consent.items === 0;
      await invoke(deleted ? 'vault_sync_forget_cloud' : 'vault_sync_disenroll').catch(() => {});
    }
    return;
  }
  if (choice === 'ask') window.dispatchEvent(new Event(CONSENT_NEEDED_EVENT));
  if (choice !== 'cloud' && !status.enrolled) return;
  try {
    if (!status.enrolled) {
      const outcome = await invoke('vault_sync_enroll', { userId: String(user.id) });
      await invoke('sync_register_vault', { scopeId: outcome.scopeId });
    } else {
      // Already enrolled, but `EngineState.tasks` is in-memory and does not
      // survive a process restart — idempotent re-registration, matching
      // `registerEnrolledCloudWorkspaces`'s own re-arm-on-every-launch
      // pattern.
      await invoke('sync_register_vault', { scopeId: status.scopeId });
    }
    // Card X1: this computer in the vault's device list (once per start).
    await registerThisDevice();
    recordFeature(VAULT.CLOUD_ON); // card 7.2: once per day at most (telemetry/dailyMark)
  } catch (err) {
    console.warn('[vault-sync] enrol/register failed:', err);
    recordFeature(VAULT.SYNC_ERROR); // the key only — never the error text
  }
}

/**
 * User-initiated "Refresh" (vault titlebar action): force a full round trip
 * NOW instead of waiting for the engine's own cadence.
 *
 * Deliberately more than `useVaultStore.refresh()`. That reads `vault_list`,
 * which reads the LOCAL table — so on its own it re-renders exactly the same
 * items and looks broken precisely when the user reaches for it, i.e. when
 * this device is missing something another device has. The four steps:
 *
 *  1. `vault_sync_reconcile` — re-queues anything stranded (a live item with
 *     no `server_seq` and no outbox row) and repairs a stale keyblock. This is
 *     the self-heal that fixes a device whose items never left it.
 *  2. `sync_pull_now` — wakes the engine task for the vault scope, which
 *     pushes the outbox and pulls remote changes into staging.
 *  3. `vault_sync_drain` — applies whatever is staged into `vault_items`.
 *     Rows that arrive from step 2 land after this returns; those are covered
 *     by `drain_on_engine_cycle`'s own `vault:changed` emit.
 *  4. reload the list.
 *
 * Steps 1-3 are skipped when the vault is not eligible (absent, unprotected or
 * locked) or not enrolled — a local-only vault has nothing to round-trip, and
 * `vault_sync_reconcile` needs the DEK. Step 4 always runs, so the button is
 * never a no-op and never throws at a signed-out user.
 *
 * Resolves either way: a refresh that fails because the network is down should
 * still show the local list, not surface a dialog. Returns true when the cloud
 * round trip actually ran, so a caller can distinguish "checked the server"
 * from "reloaded locally".
 */
export async function refreshVaultFromCloud() {
  if (!isTauri()) return false;
  let synced = false;
  try {
    const status = await invoke('vault_sync_status');
    if (status?.eligible && status?.enrolled && status?.scopeId) {
      // Each step is individually non-fatal: a failed reconcile should not
      // cost the user the pull, and a failed pull should not cost the drain.
      try {
        await invoke('vault_sync_reconcile');
      } catch (err) {
        console.warn('[vault-sync] refresh: reconcile failed:', err);
      }
      try {
        await invoke('sync_pull_now', { workspaceId: status.scopeId });
        synced = true;
      } catch (err) {
        // Expected while offline, and after a cold launch if the engine task
        // is not registered yet ("workspace not registered with the sync
        // engine") — `ensureVaultSync` arms it on the next auth settle.
        console.warn('[vault-sync] refresh: pull kick failed:', err);
      }
      try {
        await invoke('vault_sync_drain');
      } catch (err) {
        console.warn('[vault-sync] refresh: drain failed:', err);
      }
    }
  } catch (err) {
    console.warn('[vault-sync] refresh: status read failed:', err);
  }
  // Imported lazily: `vaultStore` pulls in the whole vault UI surface, and
  // this module is loaded by `useSyncEngine` at auth-settle time, long before
  // the vault panel has ever been opened.
  const { useVaultStore } = await import('../store/vaultStore');
  await useVaultStore.getState().refresh();
  return synced;
}
